import { motion } from "framer-motion";
import { Check, X, Crown } from "lucide-react";
import { SectionHead, staggerContainer, staggerItem } from "../Reveal";
import { whatsappLink } from "../../data/site";

const PLANS = [
  {
    id: "recordings",
    name: "Self-Paced Recordings",
    price: "$500",
    note: "one-time • all courses",
    tagline: "Every course recording + notes, learn at your own speed",
    cta: "Get Recordings Access",
    featured: false,
  },
  {
    id: "recordings-labs",
    name: "Recordings + Labs",
    price: "$1,200",
    note: "one-time • all courses",
    tagline: "All recordings PLUS 24x7 hands-on lab pods to practise",
    cta: "Get Labs Access",
    featured: false,
  },
  {
    id: "live",
    name: "1-on-1 Live + Labs + Recordings",
    price: "$2,100",
    note: "one-time • everything included",
    tagline: "The complete flagship — private live classes with an industry engineer",
    cta: "Go 1-on-1 Live",
    featured: true,
  },
];

const COMPARISON = [
  { feature: "All 30+ course recordings", p1: true, p2: true, p3: true },
  { feature: "Notes PDF & lab workbooks", p1: true, p2: true, p3: true },
  { feature: "Learn at your own pace", p1: true, p2: true, p3: true },
  { feature: "Course completion certificate", p1: true, p2: true, p3: true },
  { feature: "24x7 dedicated lab pod access", p1: false, p2: true, p3: true },
  { feature: "Lab guides with solutions", p1: false, p2: true, p3: true },
  { feature: "Mentor doubt support", p1: false, p2: true, p3: true },
  { feature: "Live 1-on-1 classes with engineer", p1: false, p2: false, p3: true },
  { feature: "Flexible personal schedule", p1: false, p2: false, p3: true },
  { feature: "Mock interviews with MNC rubrics", p1: false, p2: false, p3: true },
  { feature: "Resume building + placement assistance", p1: false, p2: false, p3: true },
];

const Mark = ({ yes }) =>
  yes ? (
    <Check className="w-5 h-5 text-emerald-400 mx-auto" />
  ) : (
    <X className="w-5 h-5 text-slate-600 mx-auto" />
  );

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="relative py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead
        eyebrow="All-Access Packages"
        title={
          <>
            One Package. <span className="text-gradient-sky">Every Course.</span>{" "}
            <span className="text-gradient-orange">Zero Confusion.</span>
          </>
        }
        sub="Instead of buying courses one by one, unlock the entire academy — pick how much guidance you want."
      />

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        className="grid md:grid-cols-3 gap-6 mb-16"
      >
        {PLANS.map((p) => (
          <motion.div
            key={p.id}
            variants={staggerItem}
            data-testid={`pricing-plan-${p.id}`}
            className={`relative rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:-translate-y-2 ${
              p.featured
                ? "border-orange-500/60 bg-gradient-to-b from-orange-500/15 to-[#0B1226] shadow-[0_0_45px_rgba(249,115,22,0.15)]"
                : "border-sky-500/20 bg-[#0B1226] hover:border-sky-400/50"
            }`}
          >
            {p.featured && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-orange-500 text-white font-display text-[11px] font-bold px-4 py-1.5 uppercase tracking-wider">
                <Crown className="w-3.5 h-3.5" /> Most Value
              </span>
            )}
            <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
            <p className="mt-1 text-xs text-slate-400">{p.tagline}</p>
            <p className="mt-5">
              <span className={`font-display text-4xl font-extrabold ${p.featured ? "text-gradient-orange" : "text-white"}`}>
                {p.price}
              </span>
              <span className="block mt-1 text-[11px] text-slate-500 font-mono2 uppercase tracking-wider">{p.note}</span>
            </p>
            <a
              data-testid={`pricing-cta-${p.id}`}
              href={whatsappLink(`Hi LearnWithTAC, I am interested in the "${p.name}" package (${p.price}). Please share details.`)}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-6 inline-flex items-center justify-center rounded-xl font-display font-bold text-sm px-6 py-3.5 transition-all duration-300 ${
                p.featured
                  ? "bg-orange-500 hover:bg-orange-600 text-white shadow-[0_0_25px_rgba(249,115,22,0.35)]"
                  : "border border-sky-500/40 text-sky-300 hover:text-white hover:bg-sky-500/10"
              }`}
            >
              {p.cta}
            </a>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        data-testid="pricing-comparison-table"
        className="rounded-2xl border border-sky-500/20 bg-[#0B1226] overflow-hidden"
      >
        <div className="px-6 sm:px-8 py-6 border-b border-sky-500/15">
          <h3 className="font-display text-xl font-bold text-white">
            Compare & Decide <span className="text-gradient-orange">— What's Included</span>
          </h3>
          <p className="mt-1 text-sm text-slate-400">Har feature ka clear yes/no — socho mat, compare karo.</p>
        </div>
        <div className="overflow-x-auto hidden sm:block">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-sky-500/15 text-left">
                <th className="px-6 sm:px-8 py-4 font-display font-semibold text-slate-300">Feature</th>
                <th className="px-4 py-4 text-center font-display font-semibold text-slate-300">Recordings<br /><span className="text-xs text-slate-500">$500</span></th>
                <th className="px-4 py-4 text-center font-display font-semibold text-slate-300">Rec + Labs<br /><span className="text-xs text-slate-500">$1,200</span></th>
                <th className="px-4 py-4 text-center font-display font-semibold text-orange-400">1-on-1 Live<br /><span className="text-xs text-orange-500/70">$2,100</span></th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, i) => (
                <tr
                  key={row.feature}
                  data-testid={`compare-row-${i}`}
                  className="border-b border-sky-500/10 last:border-0 hover:bg-sky-500/5 transition-colors duration-200"
                >
                  <td className="px-6 sm:px-8 py-3.5 text-slate-300">{row.feature}</td>
                  <td className="px-4 py-3.5 text-center"><Mark yes={row.p1} /></td>
                  <td className="px-4 py-3.5 text-center"><Mark yes={row.p2} /></td>
                  <td className="px-4 py-3.5 text-center"><Mark yes={row.p3} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sm:hidden divide-y divide-sky-500/10">
          {PLANS.map((p, pi) => (
            <div key={p.id} data-testid={`compare-mobile-${p.id}`} className="p-5">
              <p className="font-display font-bold text-white">
                {p.name} <span className="text-orange-400">— {p.price}</span>
              </p>
              <ul className="mt-3 space-y-2">
                {COMPARISON.map((row) => {
                  const yes = [row.p1, row.p2, row.p3][pi];
                  return (
                    <li key={row.feature} className="flex items-center gap-2.5 text-xs">
                      {yes ? (
                        <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-slate-600 shrink-0" />
                      )}
                      <span className={yes ? "text-slate-300" : "text-slate-600"}>{row.feature}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        <div className="px-6 sm:px-8 py-5 bg-[#0F1A36]/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">
            Confused which one fits you? <span className="text-white font-semibold">Free counselling call</span> — we'll recommend honestly, even if it's the cheapest plan.
          </p>
          <a
            data-testid="pricing-help-whatsapp-btn"
            href={whatsappLink("Hi LearnWithTAC, I need help choosing between the packages.")}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center rounded-xl bg-[#25D366] hover:bg-[#1fb857] text-[#060913] font-display font-bold text-sm px-6 py-3 transition-colors duration-200"
          >
            Ask on WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);
